import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStaffStore } from '../../stores/useStaffStore';
import { useCurrentStore } from '../../hooks/useCurrentStore';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, ConfirmModal } from '@redpanda/ui';
import StaffDialog from '../../components/StaffDialog';

const StaffPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useCurrentStore(); // 自動獲取當前用戶店鋪
  const {
    staff,
    isLoading,
    hasFetched,
    showDeleteConfirmModal,
    fetchStaff,
    createStaff,
    updateStaff,
    toggleStaffStatus,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
  } = useStaffStore();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);

  // 載入員工列表（當店鋪資訊載入完成後）
  useEffect(() => {
    if (storeId) {
      fetchStaff(storeId);
    }
  }, [storeId, fetchStaff]);

  const handleAddStaff = () => {
    setSelectedStaff(null);
    setIsDialogOpen(true);
  };

  const handleEditStaff = (staff: any) => {
    setSelectedStaff(staff);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedStaff(null);
  };

  const handleSubmit = async (data: any) => {
    if (!storeId) return;

    if (selectedStaff) {
      // 編輯
      await updateStaff(selectedStaff.id, data);
    } else {
      // 新增
      await createStaff(storeId, data);
    }
  };

  const handleToggleStatus = async (staff: any) => {
    try {
      await toggleStaffStatus(staff.id, !staff.isActive);
    } catch (error) {
      // Error handled in store
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('pages:admin.staff.title')}
          </h1>
          <Button
            onClick={handleAddStaff}
            className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
          >
            {t('pages:admin.staff.addStaff')}
          </Button>
        </div>
      </div>

      {/* 只在首次載入且尚未讀取過時顯示載入畫面 */}
      {isLoading && staff.length === 0 && !hasFetched ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('pages:admin.staff.loading')}
        </div>
      ) : staff.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('pages:admin.staff.noRecords')}
          </p>
          <Button
            onClick={handleAddStaff}
            className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
          >
            {t('pages:admin.staff.addFirstStaff')}
          </Button>
        </div>
      ) : (
        <>
          {/* 手機版卡片列表 */}
          <div className="md:hidden space-y-3">
            {staff.map((staffMember) => (
              <div
                key={staffMember.id}
                className={`bg-white dark:bg-slate-800 shadow rounded-lg p-3 space-y-3 ${
                  !staffMember.isActive ? 'opacity-50' : ''
                }`}
              >
                {/* 員工名稱和狀態 */}
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    {staffMember.name}
                  </h3>
                  <button
                    onClick={() => handleToggleStatus(staffMember)}
                    className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                      staffMember.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                        : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {staffMember.isActive ? t('pages:admin.staff.enabled') : t('pages:admin.staff.disabled')}
                  </button>
                </div>

                {/* 聯絡資訊 */}
                <div className="space-y-1 text-sm border-t border-gray-200 dark:border-slate-700 pt-3">
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400 w-16">{t('pages:admin.staff.mobileView.phone')}</span>
                    <span className="text-gray-900 dark:text-white">
                      {staffMember.phone || '-'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400 w-16">{t('pages:admin.staff.mobileView.email')}</span>
                    <span className="text-gray-900 dark:text-white truncate">
                      {staffMember.email || '-'}
                    </span>
                  </div>
                </div>

                {/* 操作按鈕 */}
                <div className="flex space-x-4 border-t border-gray-200 dark:border-slate-700 pt-3">
                  <button
                    onClick={() => handleEditStaff(staffMember)}
                    className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 font-medium"
                  >
                    {t('translation:common.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(staffMember.id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
                  >
                    {t('translation:common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* 桌面版表格 */}
          <div className="hidden md:block bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/5">{t('pages:admin.staff.tableHeaders.name')}</TableHead>
                  <TableHead className="w-1/5">{t('pages:admin.staff.tableHeaders.phone')}</TableHead>
                  <TableHead className="w-1/4">{t('pages:admin.staff.tableHeaders.email')}</TableHead>
                  <TableHead className="w-1/12">{t('pages:admin.staff.tableHeaders.status')}</TableHead>
                  <TableHead className="w-1/6">{t('pages:admin.staff.tableHeaders.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((staffMember) => (
                  <TableRow
                    key={staffMember.id}
                    className={!staffMember.isActive ? 'opacity-50' : ''}
                  >
                    <TableCell className="font-medium">
                      {staffMember.name}
                    </TableCell>
                    <TableCell>
                      {staffMember.phone || '-'}
                    </TableCell>
                    <TableCell>
                      {staffMember.email || '-'}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleStatus(staffMember)}
                        className={`px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                          staffMember.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                            : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {staffMember.isActive ? t('pages:admin.staff.enabled') : t('pages:admin.staff.disabled')}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-4">
                        <button
                          onClick={() => handleEditStaff(staffMember)}
                          className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                        >
                          {t('translation:common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(staffMember.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          {t('translation:common.delete')}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        </>
      )}

      {/* 員工對話框 */}
      <StaffDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        staff={selectedStaff}
        storeId={storeId}
      />

      {/* 刪除確認對話框 */}
      {showDeleteConfirmModal && (
        <ConfirmModal
          isOpen={showDeleteConfirmModal}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title={t('pages:admin.staff.confirmDelete.title')}
          message={t('pages:admin.staff.confirmDelete.message')}
          confirmText={t('pages:admin.staff.confirmDelete.confirm')}
          cancelText={t('pages:admin.staff.confirmDelete.cancel')}
          type="danger"
        />
      )}
    </div>
  );
};

export default StaffPage;
